import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Spinner, Badge, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { FaCheck, FaTimes, FaFileAlt } from 'react-icons/fa';
import { formatTimeAgo } from '../../../utils/communityUtils';

export default function AdminOperatorReviews() {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Review Form State
  const [reviewLevel, setReviewLevel] = useState('Bronze');
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchApplications();
  }, [token]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/operator-applications?status=pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setApplications(data.applications || []);
      }
    } catch (error) {
      console.error("Error fetching operator applications", error);
    } finally {
      setLoading(false);
    }
  };

  const openReviewModal = (app) => {
    setSelectedApp(app);
    setReviewLevel('Bronze');
    setReviewNotes('');
    setShowModal(true);
  };

  const handleReview = async (action) => {
    try {
      setActionLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/operator-applications/${selectedApp.id}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          action, 
          level: reviewLevel, 
          notes: reviewNotes 
        })
      });
      const data = await res.json();
      if (data.success) {
        setApplications(prev => prev.filter(a => a.id !== selectedApp.id));
        setShowModal(false);
      } else {
        alert(data.error || "Review failed");
      }
    } catch (error) {
      console.error("Error reviewing application", error);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  return (
    <div className="py-4 community-admin-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Operator Verification Applications</h2>
        <Badge bg="danger" className="fs-5">{applications.length} Pending</Badge>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>User ID</th>
                <th>Rental History</th>
                <th>Quiz Score</th>
                <th>Agreement Signed</th>
                <th>Submitted</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.length > 0 ? (
                applications.map(app => (
                  <tr key={app.id}>
                    <td>{app.userId.substring(0, 10)}...</td>
                    <td>{app.rentalHistoryCount || 0} bookings</td>
                    <td>
                      <Badge bg={app.knowledgeQuizScore >= 80 ? 'success' : 'warning'}>
                        {app.knowledgeQuizScore}%
                      </Badge>
                    </td>
                    <td>
                      {app.safetyAgreementSigned ? (
                        <FaCheck className="text-success" />
                      ) : (
                        <FaTimes className="text-danger" />
                      )}
                    </td>
                    <td>{formatTimeAgo(app.createdAt)}</td>
                    <td className="text-end">
                      <Button variant="outline-primary" size="sm" onClick={() => openReviewModal(app)}>
                        <FaFileAlt className="me-1" /> Review Application
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    No pending operator applications.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Review Operator Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApp && (
            <Row>
              <Col md={6}>
                <h5 className="border-bottom pb-2 mb-3">Applicant Data</h5>
                <div className="mb-2"><strong>User ID:</strong> {selectedApp.userId}</div>
                <div className="mb-2"><strong>Rental History:</strong> {selectedApp.rentalHistoryCount} past bookings</div>
                <div className="mb-2"><strong>Quiz Score:</strong> {selectedApp.knowledgeQuizScore}%</div>
                <div className="mb-4"><strong>Applied:</strong> {new Date(selectedApp.createdAt).toLocaleString()}</div>
                
                <h5 className="border-bottom pb-2 mb-3">Documents</h5>
                <div className="mb-2">
                  <a href={selectedApp.idDocUrl} target="_blank" rel="noopener noreferrer">View ID Document</a>
                </div>
                <div className="mb-2">
                  <a href={selectedApp.boatingLicenseUrl} target="_blank" rel="noopener noreferrer">View Boating License</a>
                </div>
              </Col>
              
              <Col md={6}>
                <h5 className="border-bottom pb-2 mb-3">Review Decision</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Assign Trust Level (if approving)</Form.Label>
                  <Form.Select 
                    value={reviewLevel} 
                    onChange={(e) => setReviewLevel(e.target.value)}
                  >
                    <option value="Bronze">Bronze (New Verified Operator)</option>
                    <option value="Silver">Silver (Experienced Operator)</option>
                    <option value="Gold">Gold (Pro Guide / Top Renter)</option>
                    <option value="Platinum">Platinum (BBR VIP)</option>
                  </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Reviewer Notes / Feedback to User</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={4} 
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Enter notes (optional)..."
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleReview('reject')} disabled={actionLoading}>
            <FaTimes className="me-1" /> Reject Application
          </Button>
          <Button variant="success" onClick={() => handleReview('approve')} disabled={actionLoading}>
            <FaCheck className="me-1" /> Approve & Verify
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
